// src/components/ResetPassword.js
import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import { confirmPasswordReset, applyActionCode } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [oobCode, setOobCode] = useState(""); // 邮件中的重置码

  const navigate = useNavigate();
  const location = useLocation();

  // 从URL中提取重置码（?oobCode=xxx）
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("oobCode");
    if (!code) {
      setErrorMsg("无效的重置链接 / Invalid reset link");
      return;
    }
    setOobCode(code);

    // 验证链接有效性
    applyActionCode(auth, code)
      .catch(err => {
        setErrorMsg("链接已过期/无效 / Link expired or invalid");
      });
  }, [location.search]);

  // 重置密码
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!oobCode) return;
    if (!newPassword || !confirmPassword) {
      setErrorMsg("请填写密码和确认密码 / Please fill in password and confirm password");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg("两次密码输入不一致 / Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      setErrorMsg("密码长度不能少于6位 / Password must be at least 6 characters");
      return;
    }
    if (loading) return;

    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      // 确认重置密码
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccessMsg("密码重置成功！即将跳转到登录页 / Password reset successful! Redirecting to login...");
      
      // 3秒后跳转到登录页
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      console.error("重置密码失败:", err);
      let msg = "重置失败 / Reset failed: ";
      if (err.code === "auth/expired-action-code") {
        msg += "链接已过期 / Link expired";
      } else if (err.code === "auth/invalid-action-code") {
        msg += "无效的重置码 / Invalid reset code";
      } else if (err.code === "auth/weak-password") {
        msg += "密码强度不足 / Weak password";
      } else {
        msg += err.message;
      }
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-pink-50 via-rose-50 to-rose-100 p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6">
        <h1 className="text-2xl font-semibold mb-4" style={{ color: "#C86B85" }}>
          🔑 Reset Password / 重置密码
        </h1>

        {/* 提示信息 */}
        {successMsg && (
          <div className="mb-4 p-2 bg-green-100 text-green-700 rounded-lg text-center">
            {successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-lg text-center">
            {errorMsg}
          </div>
        )}

        {/* 重置密码表单 */}
        <form onSubmit={handleResetPassword}>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New Password / 新密码 (≥6位)"
            className="w-full border border-gray-200 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-pink-200"
            disabled={loading || !oobCode}
            required
          />

          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm New Password / 确认新密码"
            className="w-full border border-gray-200 rounded-lg px-4 py-2 mb-6 focus:outline-none focus:ring-2 focus:ring-pink-200"
            disabled={loading || !oobCode}
            required
          />

          <button
            type="submit"
            className={`w-full py-2 rounded-lg text-white font-semibold ${loading || !oobCode ? "opacity-70 cursor-not-allowed" : ""}`}
            style={{ background: "linear-gradient(90deg,#F7C6D4,#F4D19B)" }}
            disabled={loading || !oobCode}
          >
            {loading ? "Resetting..." : "Reset Password / 重置密码"}
          </button>
        </form>

        {/* 返回登录 */}
        <div className="mt-4 text-center text-gray-600">
          <button
            onClick={() => navigate("/login")}
            className="text-pink-600 hover:underline bg-transparent border-none cursor-pointer"
          >
            ← Back to Login / 返回登录
          </button>
        </div>
      </div>
    </div>
  );
}